import {
  ApolloClient,
  ApolloClientOptions,
  ApolloProvider,
  from,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { useAuth0 } from '@auth0/auth0-react'
import { createUploadLink } from 'apollo-upload-client'
import { persistCache } from 'apollo3-cache-persist'
import localforage from 'localforage'
import React, { FC, ReactNode, useEffect, useState } from 'react'

import introspectionResult from '../../generated/fragment-matcher'

const APOLLO_CACHE_STORAGE_CONFIG = {
  name: 'wdn-admin-v2',
  storeName: 'apollo-cache',
}

const apolloCacheStorage = localforage.createInstance(
  APOLLO_CACHE_STORAGE_CONFIG,
)

async function safePersistApolloCache(cache: InMemoryCache): Promise<void> {
  const debug = process.env.NODE_ENV === 'development'

  try {
    await persistCache({
      cache,
      storage: apolloCacheStorage,
      maxSize: false,
      debug,
    })
    return
  } catch (error) {
    console.warn(
      '[apollo-cache-persist] Restore failed; purging persisted cache',
      error,
    )
  }

  try {
    await apolloCacheStorage.clear()
  } catch {
    // Ignore cleanup errors
  }

  try {
    await (localforage as any).dropInstance?.(APOLLO_CACHE_STORAGE_CONFIG)
  } catch {
    // Ignore cleanup errors
  }

  try {
    await persistCache({
      cache,
      storage: apolloCacheStorage,
      maxSize: false,
      debug,
    })
  } catch (error) {
    console.warn(
      '[apollo-cache-persist] Cache persistence disabled for this session',
      error,
    )
  }
}

const ApolloProviderConfigured: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { getAccessTokenSilently } = useAuth0()
  const [client, setClient] = useState<ApolloClient<any>>()

  useEffect(() => {
    ;(async () => {
      const API_ENDPOINT =
        process.env.REACT_APP_API_ENDPOINT || 'https://api.wdnsolutions.com'
      const API_PATH = process.env.REACT_APP_API_PATH || '/api/core'

      const link = createUploadLink({
        uri: `${API_ENDPOINT}${API_PATH}`,
        headers: { 'Cache-Control': 'no-cache' },
        fetchOptions: { timeout: 30000 },
      })

      const authMiddleware = setContext(async (_, { headers }) => {
        try {
          const token = await getAccessTokenSilently()
          return {
            headers: {
              ...headers,
              authorization: token ? `Bearer ${token}` : '',
            },
          }
        } catch {
          return { headers }
        }
      })

      const cache = new InMemoryCache({
        resultCaching: true,
        possibleTypes: introspectionResult.possibleTypes,
        typePolicies: { Query: { fields: {} } },
      })

      await safePersistApolloCache(cache)

      const apolloConfig: ApolloClientOptions<NormalizedCacheObject> = {
        link: from([authMiddleware, link]),
        cache,
        connectToDevTools: process.env.NODE_ENV === 'development',
      }

      setClient(new ApolloClient(apolloConfig))
    })()
  }, [getAccessTokenSilently])

  if (!client) return null
  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export default ApolloProviderConfigured
